import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  redirectTo?: string;
}

const LoginModal: React.FC<LoginModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  redirectTo = "/my-projects" 
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!email || !password) {
      toast({
        title: "Erro de validação",
        description: "Por favor, preencha todos os campos",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Verificar se são credenciais de parceiro
      const partnerCredentials = [
        {
          email: "avioes@empresa.com",
          password: "senha123",
          companyId: "1",
          companyName: "Aviões S.A.",
          companyType: "aviation"
        },
        {
          email: "hotel@empresa.com",
          password: "senha123",
          companyId: "2",
          companyName: "Hotel Mar do Brasil Ltda",
          companyType: "hotel"
        }
      ];

      const partner = partnerCredentials.find(
        (p) => p.email === email && p.password === password
      );

      if (partner) {
        // É um parceiro - redirecionar para portal do parceiro
        localStorage.setItem("partnerToken", "mock-token-" + partner.companyId);
        localStorage.setItem("partnerCompanyId", partner.companyId);
        localStorage.setItem("partnerCompanyName", partner.companyName);
        localStorage.setItem("partnerCompanyType", partner.companyType);
        
        toast({
          title: "Login realizado com sucesso!",
          description: `Bem-vindo, ${partner.companyName}`,
        });
        
        onClose();
        navigate("/partner/dashboard");
        setIsLoading(false);
        return;
      }
      
      // Simulação de autenticação - em uma implementação real,
      // aqui seria uma chamada a uma API de autenticação
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Login simulado - em um caso real, verificaria credenciais em um servidor
      // Para fins de demonstração, aceita qualquer email com formato válido e senha com pelo menos 6 caracteres
      if (email.includes('@') && password.length >= 6) {
        console.log('Login bem-sucedido, definindo isLoggedIn como true');
        
        // Login bem-sucedido - definir localStorage primeiro
        localStorage.setItem('isLoggedIn', 'true');
        
        // Notificação para o usuário
        toast({
          title: "Login realizado com sucesso",
          description: "Bem-vindo à página de projetos",
        });
        
        // Executar callback de sucesso se fornecido
        if (onSuccess) {
          onSuccess();
        }
        
        // Fechar o modal
        onClose();
        
        // Navegar usando React Router
        console.log('Navegando para:', redirectTo);
        navigate(redirectTo);
      } else {
        // Login falhou
        toast({
          title: "Falha no login",
          description: "Email ou senha inválidos. Por favor, tente novamente.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro no login",
        description: "Ocorreu um erro ao processar seu login.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Login</DialogTitle>
          <DialogDescription>
            Entre com suas credenciais para acessar seus projetos.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password">Senha</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          
          <DialogFooter className="pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
              disabled={isLoading}
            >
              {isLoading ? "Processando..." : "Entrar"}
            </Button>
          </DialogFooter>
        </form>
        
        <div className="text-xs text-gray-500 pt-2">
          <p>Para fins de demonstração, use qualquer email válido e senha com pelo menos 6 caracteres.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoginModal; 